import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { addBreadcrumb } from './sentry';

type PerformanceMetrics = {
  timeToFirstByte: number | null;
  timeToFirstPaint: number | null;
  timeToFirstContentfulPaint: number | null;
  domContentLoaded: number | null;
  windowLoaded: number | null;
  largestContentfulPaint: number | null;
  firstInputDelay: number | null;
  cumulativeLayoutShift: number | null;
};

const PerformanceMonitor: React.FC = () => {
  useEffect(() => {
    // Only measure performance in production
    if (!import.meta.env.PROD) {
      return;
    }

    const metrics: PerformanceMetrics = {
      timeToFirstByte: null,
      timeToFirstPaint: null,
      timeToFirstContentfulPaint: null,
      domContentLoaded: null,
      windowLoaded: null,
      largestContentfulPaint: null,
      firstInputDelay: null,
      cumulativeLayoutShift: null,
    };

    // Time to First Byte (TTFB)
    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navigationEntries.length > 0) {
      metrics.timeToFirstByte = navigationEntries[0].responseStart;
      metrics.domContentLoaded = navigationEntries[0].domContentLoadedEventEnd;
      metrics.windowLoaded = navigationEntries[0].loadEventEnd;
    }

    // First Paint and First Contentful Paint
    const paintEntries = performance.getEntriesByType('paint');
    paintEntries.forEach(entry => {
      if (entry.name === 'first-paint') {
        metrics.timeToFirstPaint = entry.startTime;
      } else if (entry.name === 'first-contentful-paint') {
        metrics.timeToFirstContentfulPaint = entry.startTime;
      }
    });

    // Record metrics as Sentry breadcrumbs
    Object.entries(metrics).forEach(([metricName, value]) => {
      if (value !== null) {
        addBreadcrumb({
          category: 'performance',
          message: `${metricName}: ${value}ms`,
          level: 'info',
        });
      }
    });

    // Largest Contentful Paint
    const observeLCP = () => {
      try {
        const observer = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          metrics.largestContentfulPaint = lastEntry.startTime;
          
          addBreadcrumb({
            category: 'performance',
            message: `largestContentfulPaint: ${metrics.largestContentfulPaint}ms`,
            level: 'info',
          });
          
          // Send performance data to Sentry
          Sentry.setTag('lcp', metrics.largestContentfulPaint?.toString() || 'unknown');
        });
        
        observer.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) {
        console.error('LCP observation error:', e);
      }
    };

    // First Input Delay
    const observeFID = () => {
      try {
        const observer = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const firstInput = entries[0];
          metrics.firstInputDelay = firstInput.processingStart - firstInput.startTime;
          
          addBreadcrumb({
            category: 'performance',
            message: `firstInputDelay: ${metrics.firstInputDelay}ms`,
            level: 'info',
          });
          
          // Send performance data to Sentry
          Sentry.setTag('fid', metrics.firstInputDelay?.toString() || 'unknown');
        });
        
        observer.observe({ type: 'first-input', buffered: true });
      } catch (e) {
        console.error('FID observation error:', e);
      }
    };

    // Cumulative Layout Shift
    const observeCLS = () => {
      try {
        let clsValue = 0;
        let clsEntries: PerformanceEntry[] = [];
        
        const observer = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          
          entries.forEach(entry => {
            // Only count layout shifts without recent user input
            if (!(entry as any).hadRecentInput) {
              const value = (entry as any).value;
              clsValue += value;
              clsEntries.push(entry);
            }
          });
          
          metrics.cumulativeLayoutShift = clsValue;
          
          addBreadcrumb({
            category: 'performance',
            message: `cumulativeLayoutShift: ${metrics.cumulativeLayoutShift}`,
            level: 'info',
          });
          
          // Send performance data to Sentry
          Sentry.setTag('cls', metrics.cumulativeLayoutShift?.toString() || 'unknown');
        });
        
        observer.observe({ type: 'layout-shift', buffered: true });
      } catch (e) {
        console.error('CLS observation error:', e);
      }
    };

    // Start observations after a small delay to prioritize critical rendering
    setTimeout(() => {
      observeLCP();
      observeFID();
      observeCLS();
    }, 3000);

    // Send all metrics to Sentry on unload
    window.addEventListener('unload', () => {
      // Create a transaction for the page visit
      const transaction = Sentry.startTransaction({
        name: 'page-metrics',
        op: 'pageload',
      });
      
      // Add measurements to the transaction
      Object.entries(metrics).forEach(([metricName, value]) => {
        if (value !== null) {
          transaction.setMeasurement(metricName, value, 'millisecond');
        }
      });
      
      // Finish the transaction
      transaction.finish();
    });

  }, []);

  // This component doesn't render anything
  return null;
};

export default PerformanceMonitor;
